import React from 'react';
import {
  EnvelopeIcon,
  DocumentTextIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  UserIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  CalendarIcon,
  ArrowUpTrayIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import type { SuggestedAction } from '../../types/chat';

interface Props {
  actions: SuggestedAction[];
  onAction: (action: SuggestedAction) => void;
  disabled?: boolean;
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  mail: EnvelopeIcon,
  'file-text': DocumentTextIcon,
  radar: SignalIcon,
  alert: ExclamationTriangleIcon,
  user: UserIcon,
  download: ArrowDownTrayIcon,
  'check-square': CheckCircleIcon,
  calendar: CalendarIcon,
  upload: ArrowUpTrayIcon,
  slack: ChatBubbleLeftRightIcon,
};

function ActionIcon({ name }: { name?: string }) {
  const Icon = (name && ICON_MAP[name]) || DocumentTextIcon;
  return <Icon className="h-3.5 w-3.5 flex-shrink-0" />;
}

const ChatActionBar: React.FC<Props> = ({ actions, onAction, disabled = false }) => {
  if (!actions?.length) return null;

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {actions.map((action) => {
        const isDisabled = disabled || action.kind === 'disabled';
        const tooltip = action.disabled_reason || (isDisabled ? 'Coming soon' : action.label);

        return (
          <button
            key={action.id}
            type="button"
            title={tooltip}
            disabled={isDisabled}
            onClick={() => !isDisabled && onAction(action)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              isDisabled
                ? 'cursor-not-allowed border-border/60 bg-muted/40 text-muted-foreground/60'
                : 'border-primary/30 bg-primary/5 text-primary hover:border-primary/50 hover:bg-primary/10'
            }`}
          >
            <ActionIcon name={action.icon} />
            <span className="max-w-[220px] truncate">{action.label}</span>
            {isDisabled && action.kind === 'disabled' && (
              <span className="text-[10px] uppercase tracking-wide opacity-70">Soon</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ChatActionBar;
